import React, { useState } from 'react';
import Hero from './Hero';
import Features from './Features';
import AgentSpotlight from './AgentSpotlight';
import HowItWorks from './HowItWorks';
import Benefits from './Benefits';
import AgentSelector from './AgentSelector';
import AgentPlayground from './AgentPlayground';
import ROICalculator from './ROICalculator';
import Testimonials from './Testimonials';
import FAQ from './FAQ';
import ContactForm from './ContactForm';
import WaitlistModal from './WaitlistModal';
import SuccessModal from './SuccessModal';
import { submitAllData } from '../utils/api';
import '../styles/LandingPage.css';

const LandingPage = () => {
  // State for showing modals
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false); // Track if form has been submitted
  
  // Simplified data collection - only track essential information
  const [formData, setFormData] = useState({
    contact: null,       // Contact form data
    selectedAgents: [],  // Selected agents from AgentSelector
    waitlist: null,      // Waitlist form data
    userInteractions: [] // Minimal tracking of key user interactions
  });
  
  // Track only important user interactions
  const trackInteraction = (interactionType, details = {}) => {
    // Only track key interactions we care about
    if (['agent_selected', 'form_submitted', 'section_viewed'].includes(interactionType)) {
      setFormData(prevData => ({
        ...prevData,
        userInteractions: [
          ...prevData.userInteractions,
          {
            type: interactionType,
            timestamp: new Date().toISOString(),
            details
          }
        ].slice(-10) // Only keep the 10 most recent interactions
      }));
    }
  };
  
  // Track playground interactions
  const trackPlaygroundInteraction = (agent, input, response) => {
    // Only track minimal data about playground usage
    setFormData(prevData => ({
      ...prevData,
      userInteractions: [
        ...prevData.userInteractions,
        {
          type: 'playground_used',
          timestamp: new Date().toISOString(),
          details: { agent, hasInput: !!input }
        }
      ]
    }));
  };
  
  // Update calculator inputs
  const updateCalculatorInput = (calculatorData) => {
    // Only store the most important calculator values
    const simplifiedData = {
      employeeCost: calculatorData?.inputs?.employeeCost,
      employeeHours: calculatorData?.inputs?.employeeHours,
      teamSize: calculatorData?.inputs?.teamSize
    };
    
    setFormData(prevData => ({
      ...prevData,
      userInteractions: [
        ...prevData.userInteractions,
        {
          type: 'calculator_used',
          timestamp: new Date().toISOString(),
          details: { values: { inputs: simplifiedData } }
        }
      ]
    }));
  };
  
  // Handle contact form submission (first step)
  const handleContactSubmit = (contactData) => {
    setFormData(prevData => ({
      ...prevData,
      contact: contactData
    }));
    
    trackInteraction('form_submitted', { form: 'contact' });
    setShowWaitlistModal(true);
  };
  
  // Handle agent selection
  const handleAgentSelectorContinue = (agents) => {
    setFormData(prevData => ({
      ...prevData,
      selectedAgents: agents
    }));
    
    trackInteraction('agent_selected', { count: agents.length });
    document.getElementById('contact-section').scrollIntoView({ behavior: 'smooth' });
  };
  
  // Handle waitlist form submission - THIS IS WHERE WE SEND ALL DATA
  const handleWaitlistSubmit = async (waitlistData) => {
    // Prevent multiple submissions
    if (isSubmitting || hasSubmitted) return;
    
    // Add waitlist data
    const finalFormData = {
      ...formData,
      waitlist: waitlistData
    };
    
    setIsSubmitting(true);
    
    try {
      // Send only the essential collected data
      await submitAllData(finalFormData);
      
      console.log('Form data successfully submitted');
      setHasSubmitted(true); // Mark as submitted
      
      // Show success modal
      setShowWaitlistModal(false);
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error submitting form data:', error);
      
      // Still show success modal to not block user experience
      setShowWaitlistModal(false);
      setShowSuccessModal(true);
      setHasSubmitted(true); // Even if there was an error, prevent resubmission
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="landing-page">
      <Hero onInteract={(action) => trackInteraction('hero_interaction', { action })} />
      <Features onFeatureInteract={(feature) => trackInteraction('feature_interaction', { feature })} />
      <AgentSpotlight 
        title="AI Influencer Outreach Manager" 
        description="Connect with 200+ relevant affiliates in under a minute" 
        imageUrl="/images/influencer-agent.jpg" 
        reversed={false}
        onInteract={() => trackInteraction('spotlight_interaction', { agent: 'influencer' })}
      />
      <AgentPlayground onInteraction={trackPlaygroundInteraction} />
      <AgentSpotlight 
        title="Social Media Automation Suite" 
        description="Create and schedule content across Instagram, YouTube, LinkedIn, TikTok, and Facebook with AI" 
        imageUrl="/images/social-agent.jpg" 
        reversed={true}
        onInteract={() => trackInteraction('spotlight_interaction', { agent: 'social' })}
      />
      <HowItWorks onInteract={() => trackInteraction('how_it_works_viewed')} />
      <ROICalculator onCalculate={updateCalculatorInput} />
      <Benefits onInteract={() => trackInteraction('benefits_viewed')} />
      <AgentSelector onContinue={handleAgentSelectorContinue} />
      <Testimonials onInteract={() => trackInteraction('testimonials_viewed')} />
      <FAQ onQuestionClick={(question) => trackInteraction('faq_question_clicked', { question })} />
      <ContactForm onSubmit={handleContactSubmit} />
      
      {showWaitlistModal && (
        <WaitlistModal 
          onClose={() => setShowWaitlistModal(false)}
          onSubmit={handleWaitlistSubmit}
          preSelectedAgents={formData.selectedAgents}
          isSubmitting={isSubmitting}
          hasSubmitted={hasSubmitted} // Pass down submission state
        />
      )}
      
      {showSuccessModal && (
        <SuccessModal onClose={() => setShowSuccessModal(false)} />
      )}
    </div>
  );
};

export default LandingPage;
